/**
 * Read-only working sheet for one auction run.
 *
 * Renders every vehicle decorated with computed fields + alerts. The table
 * is the lean at-a-glance view (V2 spec); the per-component costs, Lane, and
 * Store live in the detail modal, not here:
 *
 *   Status | Run # | Age | Stock # | VIN | Year | Make | Model | Color | Odo
 *   | Store | Appraiser | KBB Lending | KBB Trade | Total Cost | Sold
 *   | Disposition | High Bid | Best When | Notes
 *
 * The per-rooftop disposition columns are NOT shown here (Store + Disposition
 * cover it); they remain in the Excel export.
 *
 * Column visual styling cites canonical: components/audit/AuditTable.tsx
 * — `headerCellStyle` (line ~205) and `dataCellStyle` (line ~238).
 *
 * The table is a read-only representation of the working sheet. Editing
 * happens in VehicleDetailModal: clicking a row opens it with the full
 * cost panel (each cost editable + include-in-total checkbox), the store
 * dropdown, and the auction-result fields, with an Apply button.
 *
 * The Status column shows ✓ for clean rows and ✗ for rows with alerts.
 * Clicking ✗ opens AuctionRowAlertModal with the per-row issue list
 * (it stops propagation so it doesn't also open the detail modal).
 */
import React, { useEffect, useState } from "react"

import { AuctionMatchState, AuctionRunDetail, AuctionVehicle } from "../../api/wholesaleAuction"
import SearchInput from "../audit/SearchInput"
import DownloadButton from "./DownloadButton"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"
import { formatInt, formatMoney } from "../../utils/wholesaleAuctionFormat"
import AuctionRowAlertModal from "./AuctionRowAlertModal"
import VehicleDetailModal from "./VehicleDetailModal"

interface Props {
  detail: AuctionRunDetail
}

interface Column {
  id: string
  label: string
  // Max width cap (px) for long-text columns; numeric columns size to content.
  width?: number
  // When true, this column flexes to absorb the table's leftover width (so the
  // table fills the container instead of leaving a gap on the right).
  grow?: boolean
  align?: "left" | "right" | "center"
  render: (vehicle: AuctionVehicle) => React.ReactNode
}

type PageSize = number | "all"
const PAGE_SIZE_OPTIONS: PageSize[] = [25, 50, 100, "all"]
const DEFAULT_PAGE_SIZE: PageSize = 50

// Rooftop-filter sentinel for cars that don't route to any rooftop.
const ROOFTOP_NONE = "__none__"

export default function AuctionRunTable({ detail }: Props) {
  // Two row-triggered modals: the ✗ glyph opens the issues popup; clicking
  // anywhere else on a row opens the full detail/edit modal.
  const [alertVehicleId, setAlertVehicleId] = useState<number | null>(null)
  const [detailVehicleId, setDetailVehicleId] = useState<number | null>(null)

  // Free-text filter across the run (VIN / stock / make / model / appraiser /
  // notes …). Client-side over the already-loaded run.
  const [query, setQuery] = useState("")
  // Rooftop filter — "" = all, ROOFTOP_NONE = cars not routed to any rooftop.
  const [rooftopFilter, setRooftopFilter] = useState("")

  // Client-side paging — the whole run is already loaded, so this is pure
  // slicing of the (filtered) vehicles (modals still look up the full list).
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(0)

  // The wide table is unusable on phones — swap it for a card list (the same
  // "tial flow" as Image Index). Both share the pagination + modals below.
  const isMobile = useIsMobile()

  // Reset to the first page when the run changes or a filter narrows.
  useEffect(() => setPage(0), [detail.run.id, query, rooftopFilter])

  const cellsLocked = detail.run.status === "completed"
  const columns = buildColumns(setAlertVehicleId)
  const findVehicle = (id: number | null) =>
    id != null ? detail.vehicles.find((v) => v.id === id) ?? null : null
  const alertVehicle = findVehicle(alertVehicleId)
  const detailVehicle = findVehicle(detailVehicleId)

  const filtered = detail.vehicles.filter(
    (v) => matchesSearch(v, query) && matchesRooftop(v, rooftopFilter),
  )
  const total = filtered.length
  const perPage = pageSize === "all" ? total || 1 : pageSize
  const pageCount = Math.max(1, Math.ceil(total / perPage))
  const currentPage = Math.min(page, pageCount - 1)
  const start = currentPage * perPage
  const end = pageSize === "all" ? total : Math.min(start + perPage, total)
  const pageVehicles = filtered.slice(start, end)

  return (
    <div>
      <div style={rooftopRowStyle}>
        <select
          value={rooftopFilter}
          onChange={(e) => setRooftopFilter(e.target.value)}
          style={rooftopSelectStyle}
          aria-label="Filter by rooftop"
        >
          <option value="">All Rooftops</option>
          {detail.rooftops.map((label) => (
            <option key={label} value={label}>{label}</option>
          ))}
          <option value={ROOFTOP_NONE}>No rooftop</option>
        </select>
        {/* Download sits to the right of the rooftop filter. */}
        <DownloadButton runId={detail.run.id} runStartedAt={detail.run.started_at} />
      </div>

      <div style={searchRowStyle}>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search this run — VIN, stock #, make, model, appraiser…"
        />
      </div>

      {total === 0 ? (
        <div style={cardEmptyStyle}>
          {query
            ? "No vehicles match your search."
            : "No vehicles in this run."}
        </div>
      ) : isMobile ? (
        <AuctionCardList
          vehicles={pageVehicles}
          onCardClick={setDetailVehicleId}
          onOpenAlerts={setAlertVehicleId}
        />
      ) : (
      <div
        style={{
          overflowX: "auto",
          border: `1px solid ${THEME.colors.silver}`,
          borderRadius: THEME.borderRadius.md,
          background: THEME.colors.neutral.light.surface,
        }}
      >
        <table
          style={{
            // Fill the container, but columns size to their content (caps via
            // maxWidth) — the lone `grow` column (Notes) absorbs the leftover
            // so numeric columns stay tight without a gap on the right.
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: THEME.fonts.primary,
            fontSize: 12,
          }}
        >
          <thead>
            <tr style={{ background: THEME.auditTable.headerBackground }}>
              {columns.map((col) => (
                <th key={col.id} style={headerCellStyle(col)}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageVehicles.map((vehicle, i) => (
              <tr
                key={vehicle.id}
                onClick={() => setDetailVehicleId(vehicle.id)}
                title="Click to view / edit details"
                style={{
                  cursor: "pointer",
                  background:
                    i % 2 === 0
                      ? THEME.auditTable.zebraLight
                      : THEME.auditTable.zebraMain,
                }}
              >
                {columns.map((col) => {
                  // The whole Status cell is the click target (the glyph alone
                  // is a small target) — clicking it opens the picker / issues.
                  const statusCell = col.id === "status" && isStatusClickable(vehicle)
                  return (
                    <td
                      key={col.id}
                      style={statusCell ? { ...dataCellStyle(col), cursor: "pointer" } : dataCellStyle(col)}
                      title={statusCell ? "Click for status details" : undefined}
                      onClick={
                        statusCell
                          ? (e) => {
                              e.stopPropagation()
                              setAlertVehicleId(vehicle.id)
                            }
                          : undefined
                      }
                    >
                      {col.render(vehicle)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <PaginationBar
        total={total}
        start={total === 0 ? 0 : start + 1}
        end={end}
        pageSize={pageSize}
        onPageSize={(size) => {
          setPageSize(size)
          setPage(0)
        }}
        page={currentPage}
        pageCount={pageCount}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
      />

      {alertVehicle && (
        <AuctionRowAlertModal
          vehicle={alertVehicle}
          onClose={() => setAlertVehicleId(null)}
        />
      )}

      {detailVehicle && (
        <VehicleDetailModal
          vehicle={detailVehicle}
          locked={cellsLocked}
          rooftops={detail.rooftops}
          onClose={() => setDetailVehicleId(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

function buildColumns(
  onOpenAlerts: (vehicleId: number) => void,
): Column[] {
  // Status column comes first — clicking the ✗ opens the issues modal.
  const statusCol: Column = {
    id: "status",
    label: "Status",
    width: 64,
    align: "center",
    render: (v) => <StatusGlyph vehicle={v} onOpenAlerts={() => onOpenAlerts(v.id)} />,
  }

  // Adesa identity. Lane and the component costs live in the detail modal,
  // not the table — the table is the at-a-glance view.
  const identityCols: Column[] = [
    { id: "run_number",   label: "Run #",     width: 60,  align: "right",  render: (v) => v.run_number ?? "" },
    { id: "age",          label: "Age",       width: 50,  align: "right",  render: (v) => v.age ?? "" },
    // "Stock #" is the dealer's stock number (Adesa CLIENT ID #, stored as
    // client_id) — the number Waylon recognizes. NOT adesa_stock, which is
    // the auction-house sequence number.
    { id: "stock",        label: "Stock #",   width: 90,  render: (v) => v.client_id ?? "" },
    { id: "vin",          label: "VIN",       width: 150, render: (v) => <Mono>{v.vin ?? ""}</Mono> },
  ]

  // Vehicle details — VAUTO first, fall back to Adesa.
  const detailCols: Column[] = [
    { id: "year",  label: "Year",  width: 60,  align: "right", render: (v) => pref(v.year_vauto, v.year_adesa) ?? "" },
    { id: "make",  label: "Make",  width: 90,  render: (v) => pref(v.make_vauto, v.make_adesa) ?? "" },
    { id: "model", label: "Model", width: 130, render: (v) => pref(v.model_vauto, v.model_adesa) ?? "" },
    { id: "color", label: "Color", width: 90,  render: (v) => pref(v.color_vauto, v.color_adesa) ?? "" },
    { id: "odo",   label: "Odo",   width: 80,  align: "right", render: (v) => formatInt(pref(v.odometer_vauto, v.odo_adesa)) },
  ]

  // Store (from vAuto, matching the export) sits just left of Appraiser, then
  // appraiser + book values, the single Total Cost (its components live in the
  // modal), Sold, and Disposition.
  const valueCols: Column[] = [
    // Short rooftop label (e.g. "Toyota"); fall back to the raw vAuto store
    // when it isn't mapped to a rooftop, so unmapped stores still show.
    { id: "store",      label: "Store",     width: 120, render: (v) => v.rooftop_label ?? v.vauto_store ?? "" },
    { id: "appraiser",  label: "Appraiser", width: 130, render: (v) => v.appraiser ?? "" },
    { id: "kbb_lending", label: "KBB Lending", width: 90, align: "right", render: (v) => formatMoney(v.kbb_lending) },
    { id: "kbb_trade",   label: "KBB Trade",   width: 90, align: "right", render: (v) => formatMoney(v.kbb_trade_in) },
    { id: "total_cost",  label: "Total Cost",  width: 90, align: "right", render: (v) => formatMoney(v.total_cost) },
    { id: "sold",        label: "Sold",        width: 90, align: "right", render: (v) => formatMoney(v.sold) },
    { id: "disposition", label: "Disposition", width: 100, align: "right", render: (v) => formatMoney(v.disposition) },
  ]

  // Per-rooftop disposition columns are intentionally NOT shown here — the
  // Store column already names each car's rooftop and Disposition holds the
  // value. The per-rooftop breakout stays in the Excel export.
  const tailCols: Column[] = [
    { id: "high_bid",  label: "High Bid",  width: 90,  align: "right", render: (v) => formatMoney(v.high_bid) },
    { id: "best_when", label: "Best When", width: 90,  render: (v) => v.best_when ?? "" },
    // Notes flexes to fill the table's leftover width (no max cap).
    { id: "notes",     label: "Notes",     grow: true, render: (v) => v.notes ?? "" },
  ]

  return [
    statusCol,
    ...identityCols,
    ...detailCols,
    ...valueCols,
    ...tailCols,
  ]
}

// ---------------------------------------------------------------------------
// Cell renderers
// ---------------------------------------------------------------------------

// A row's status is actionable (the cell opens a popup) when there's something
// to review: an alert (ambiguous / no_match / unmapped rooftop), candidates to
// pick from, or any user-resolved (blue) row — so a blue ✓ is always clickable
// even when it was resolved manually with no candidates to re-pick.
function isStatusClickable(vehicle: AuctionVehicle): boolean {
  return (
    vehicle.alerts.length > 0 ||
    (vehicle.match_candidates?.length ?? 0) > 0 ||
    vehicle.match_state === "user_selected"
  )
}

// Visual per VAUTO match state. green ✓ = matched; blue ✓ = user-resolved;
// red ✗ = ambiguous (pick a VAUTO candidate) or no match. Rapid Recon notes
// follow the VAUTO match, so the glyph tracks VAUTO alone.
const STATUS_GLYPHS: Record<AuctionMatchState, { glyph: string; color: string; label: string }> = {
  matched: { glyph: "✓", color: THEME.colors.semantic.success, label: "VAUTO matched" },
  user_selected: { glyph: "✓", color: THEME.colors.semantic.info, label: "Resolved — VAUTO match selected" },
  ambiguous: { glyph: "✗", color: THEME.colors.semantic.danger, label: "Ambiguous VAUTO match — click to choose" },
  no_match: { glyph: "✗", color: THEME.colors.semantic.danger, label: "No VAUTO match — click for details" },
}

function StatusGlyph({
  vehicle,
  onOpenAlerts,
}: {
  vehicle: AuctionVehicle
  onOpenAlerts: () => void
}) {
  // Drive the glyph off match_state; fall back to alert presence for any
  // pre-migration row that has no match_state.
  const state: AuctionMatchState =
    vehicle.match_state ?? (vehicle.alerts.length > 0 ? "no_match" : "matched")
  const { glyph, color, label } = STATUS_GLYPHS[state]

  const clickable = isStatusClickable(vehicle)

  const glyphStyle: React.CSSProperties = {
    color,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
  }

  if (!clickable) {
    return (
      <span aria-label={label} style={glyphStyle}>
        {glyph}
      </span>
    )
  }
  return (
    <button
      type="button"
      // Stop the row's onClick (detail modal) so the glyph opens its own popup.
      onClick={(e) => {
        e.stopPropagation()
        onOpenAlerts()
      }}
      title={label}
      style={{ ...glyphStyle, background: "none", border: "none", padding: 0, cursor: "pointer" }}
    >
      {glyph}
    </button>
  )
}

function Mono({ children }: { children: React.ReactNode }) {
  // Canonical VIN monospace stack — cite components/audit/AuditTable.tsx
  // body cell + components/wholesale/MatchResultsTable.tsx. Inherits the
  // cell's 12px size rather than a bespoke override.
  return (
    <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Mobile card list — cite the Image Index card pattern
// (pages/photos/ImageIndexPage.tsx VehicleCardList + card* styles, ~line 407).
// ---------------------------------------------------------------------------

function AuctionCardList({
  vehicles,
  onCardClick,
  onOpenAlerts,
}: {
  vehicles: AuctionVehicle[]
  onCardClick: (vehicleId: number) => void
  onOpenAlerts: (vehicleId: number) => void
}) {
  if (vehicles.length === 0) {
    return <div style={cardEmptyStyle}>No vehicles in this run.</div>
  }
  return (
    <div style={cardListStyle}>
      {vehicles.map((v) => (
        <div
          key={v.id}
          data-testid="auction-card"
          onClick={() => onCardClick(v.id)}
          style={cardStyle}
        >
          <div style={cardHeaderStyle}>
            <StatusGlyph vehicle={v} onOpenAlerts={() => onOpenAlerts(v.id)} />
            <span style={cardRunStyle}>{cardRunLabel(v)}</span>
          </div>
          <div style={cardTitleStyle}>{cardVehicleTitle(v)}</div>
          <div style={cardIdStyle}>{cardIdLabel(v)}</div>
          <div style={cardSummaryStyle}>
            <CardMetric label="Total Cost" value={formatMoney(v.total_cost)} />
            <CardMetric label="Sold" value={formatMoney(v.sold)} />
            <CardMetric label="Disposition" value={formatMoney(v.disposition)} />
          </div>
        </div>
      ))}
    </div>
  )
}

function CardMetric({ label, value }: { label: string; value: string }) {
  return (
    <div style={cardMetricStyle}>
      <span style={cardMetricLabelStyle}>{label}</span>
      <span style={cardMetricValueStyle}>{value || "—"}</span>
    </div>
  )
}

function cardRunLabel(v: AuctionVehicle): string {
  return [
    v.run_number != null ? `Run ${v.run_number}` : null,
    v.lane ? `Lane ${v.lane}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ") || "—"
}

function cardVehicleTitle(v: AuctionVehicle): string {
  return [
    pref(v.year_vauto, v.year_adesa),
    pref(v.make_vauto, v.make_adesa),
    pref(v.model_vauto, v.model_adesa),
  ]
    .filter(Boolean)
    .join(" ") || "Vehicle"
}

function cardIdLabel(v: AuctionVehicle): string {
  return [
    v.client_id ? `Stock ${v.client_id}` : null,
    v.vin ? `VIN ${v.vin}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ") || "—"
}

const cardListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
}

const cardStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  fontFamily: THEME.fonts.primary,
  cursor: "pointer",
  WebkitTapHighlightColor: "rgba(0,0,0,0.05)",
}

const cardHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.sm,
  marginBottom: THEME.spacing.xs,
}

const cardRunStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: 2,
}

const cardIdStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: THEME.spacing.sm,
}

const cardSummaryStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.lg,
  borderTop: `1px solid ${THEME.colors.silver}`,
  paddingTop: THEME.spacing.sm,
}

const cardMetricStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
}

const cardMetricLabelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.neutral.light.textSecondary,
}

const cardMetricValueStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

const cardEmptyStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  padding: THEME.spacing.lg,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  textAlign: "center",
}

// ---------------------------------------------------------------------------
// Styling — cite components/audit/AuditTable.tsx header + body cells.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

function PaginationBar({
  total,
  start,
  end,
  pageSize,
  onPageSize,
  page,
  pageCount,
  onPrev,
  onNext,
}: {
  total: number
  start: number
  end: number
  pageSize: PageSize
  onPageSize: (size: PageSize) => void
  page: number
  pageCount: number
  onPrev: () => void
  onNext: () => void
}) {
  return (
    <div style={paginationBarStyle}>
      <label style={{ display: "flex", alignItems: "center", gap: THEME.spacing.xs }}>
        <span style={pageMutedStyle}>Rows</span>
        <select
          value={String(pageSize)}
          onChange={(e) => onPageSize(e.target.value === "all" ? "all" : Number(e.target.value))}
          style={pageSizeSelectStyle}
        >
          {PAGE_SIZE_OPTIONS.map((opt) => (
            <option key={String(opt)} value={String(opt)}>
              {opt === "all" ? "All" : opt}
            </option>
          ))}
        </select>
      </label>

      <span style={pageMutedStyle}>
        {total === 0 ? "No rows" : `Showing ${start}–${end} of ${total}`}
      </span>

      <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm }}>
        <button type="button" onClick={onPrev} disabled={page <= 0} style={pagerButtonStyle(page > 0)}>
          Prev
        </button>
        <span style={pageMutedStyle}>Page {page + 1} of {pageCount}</span>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= pageCount - 1}
          style={pagerButtonStyle(page < pageCount - 1)}
        >
          Next
        </button>
      </div>
    </div>
  )
}

const rooftopRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: THEME.spacing.sm,
  marginBottom: THEME.spacing.sm,
  // Wrap on narrow screens so the rooftop filter + Download never overflow.
  flexWrap: "wrap",
}

const rooftopSelectStyle: React.CSSProperties = {
  fontFamily: THEME.fonts.primary,
  fontSize: 13,
  // Match the Download button's size (DownloadButton buttonStyle: 150×34).
  height: 34,
  width: 150,
  boxSizing: "border-box",
  padding: `0 ${THEME.spacing.sm}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  outline: "none",
}

const searchRowStyle: React.CSSProperties = {
  display: "flex",
  marginBottom: THEME.spacing.sm,
}

const paginationBarStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: THEME.spacing.sm,
  marginTop: THEME.spacing.sm,
  fontFamily: THEME.fonts.primary,
}

const pageMutedStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
}

const pageSizeSelectStyle: React.CSSProperties = {
  fontFamily: THEME.fonts.primary,
  fontSize: 12,
  padding: "4px 6px",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  outline: "none",
}

function pagerButtonStyle(enabled: boolean): React.CSSProperties {
  return {
    padding: `4px ${THEME.spacing.sm}`,
    fontSize: 12,
    fontFamily: THEME.fonts.primary,
    background: THEME.colors.neutral.light.surface,
    color: enabled
      ? THEME.colors.neutral.light.textPrimary
      : THEME.colors.neutral.light.textSecondary,
    border: `1px solid ${THEME.colors.silver}`,
    borderRadius: THEME.borderRadius.sm,
    cursor: enabled ? "pointer" : "default",
    opacity: enabled ? 1 : 0.5,
  }
}

// ---------------------------------------------------------------------------
// Cell styling
// ---------------------------------------------------------------------------

// Columns size to their content; `col.width` is only a MAX cap (long-text
// columns like Notes/VIN ellipsize), so numeric columns collapse to the
// header/value width and dead space shrinks.
function headerCellStyle(col: Column): React.CSSProperties {
  return {
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: THEME.colors.white,
    background: THEME.auditTable.headerBackground,
    textAlign: (col.align ?? "left") as React.CSSProperties["textAlign"],
    whiteSpace: "nowrap",
    ...(col.grow ? { width: "100%" } : { maxWidth: col.width }),
    borderRight: `1px solid rgba(255, 255, 255, 0.1)`,
    borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
  }
}

function dataCellStyle(col: Column): React.CSSProperties {
  return {
    padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
    fontSize: 12,
    color: THEME.colors.neutral.light.textPrimary,
    textAlign: (col.align ?? "left") as React.CSSProperties["textAlign"],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...(col.grow ? { width: "100%" } : { maxWidth: col.width }),
    borderRight: `1px solid ${THEME.auditTable.borderColor}`,
    borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
  }
}

// ---------------------------------------------------------------------------
// Formatting + helpers
// ---------------------------------------------------------------------------

function pref<T>(primary: T | null, fallback: T | null): T | null {
  // VAUTO data is authoritative when present; fall back to Adesa.
  return primary != null ? primary : fallback
}

// Filter a vehicle by the rooftop it routes to. "" = all rooftops;
// ROOFTOP_NONE = cars with no rooftop (no match / unmapped, no override).
function matchesRooftop(vehicle: AuctionVehicle, filter: string): boolean {
  if (!filter) return true
  if (filter === ROOFTOP_NONE) return vehicle.rooftop_label == null
  return vehicle.rooftop_label === filter
}

// Free-text match across a vehicle's identity / detail fields (case-insensitive
// substring). Mirrors what the columns surface — VIN, stock, make/model, etc.
function matchesSearch(vehicle: AuctionVehicle, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const haystack = [
    vehicle.client_id,
    vehicle.vin,
    vehicle.lane,
    vehicle.run_number,
    pref(vehicle.year_vauto, vehicle.year_adesa),
    pref(vehicle.make_vauto, vehicle.make_adesa),
    pref(vehicle.model_vauto, vehicle.model_adesa),
    pref(vehicle.color_vauto, vehicle.color_adesa),
    vehicle.appraiser,
    vehicle.vauto_store,
    vehicle.best_when,
    vehicle.notes,
  ]
    .filter((field) => field != null && field !== "")
    .join(" ")
    .toLowerCase()
  return haystack.includes(needle)
}
